import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from "react";

export interface CookieConsent {
  essential: boolean; // Always true, required for site to function
  analytics: boolean; // Matomo/Analytics
  functional: boolean; // Always true - reCAPTCHA, Mapbox are now essential
  timestamp: number;
  version: string;
}

const CONSENT_STORAGE_KEY = "eureka_cookie_consent";
const CONSENT_VERSION = "1.1"; // Version bump to reset consents with new essential classification

const defaultConsent: CookieConsent = {
  essential: true,
  analytics: false,
  functional: true, // Now always true since Mapbox/reCAPTCHA are essential
  timestamp: 0,
  version: CONSENT_VERSION,
};

export const useCookieConsent = () => {
  const [consent, setConsentState] = useState<CookieConsent | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load consent from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as CookieConsent;
        // Check if version matches
        if (parsed.version === CONSENT_VERSION) {
          setConsentState(parsed);
        } else {
          // Version mismatch, reset consent
          setConsentState(null);
        }
      }
    } catch (error) {
      console.error("Error loading cookie consent:", error);
    }
    setIsLoading(false);
  }, []);

  // Save consent to localStorage
  const setConsent = useCallback((newConsent: Partial<Omit<CookieConsent, "essential" | "timestamp" | "version">>) => {
    const fullConsent: CookieConsent = {
      essential: true, // Always true
      analytics: newConsent.analytics ?? false,
      functional: true, // Always true - Mapbox/reCAPTCHA are now essential
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };
    
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(fullConsent));
      setConsentState(fullConsent);
    } catch (error) {
      console.error("Error saving cookie consent:", error);
    }
  }, []);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    setConsent({
      analytics: true,
      functional: true,
    });
  }, [setConsent]);

  // Reject all optional cookies (only analytics, functional is now essential)
  const rejectAll = useCallback(() => {
    setConsent({
      analytics: false,
      functional: true, // Always true - essential
    });
  }, [setConsent]);

  // Check if a specific category is consented
  const hasConsent = useCallback((category: keyof Omit<CookieConsent, "timestamp" | "version">) => {
    if (!consent) return category === "essential"; // Essential always allowed
    return consent[category];
  }, [consent]);

  // Check if user has made a choice
  const hasUserConsented = consent !== null;

  // Revoke consent (reset to default)
  const revokeConsent = useCallback(() => {
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      setConsentState(null);
    } catch (error) {
      console.error("Error revoking cookie consent:", error);
    }
  }, []);

  return {
    consent,
    isLoading,
    hasUserConsented,
    setConsent,
    acceptAll,
    rejectAll,
    hasConsent,
    revokeConsent,
  };
};

// Context for global access

interface CookieConsentContextType {
  consent: CookieConsent | null;
  isLoading: boolean;
  hasUserConsented: boolean;
  setConsent: (consent: Partial<Omit<CookieConsent, "essential" | "timestamp" | "version">>) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  hasConsent: (category: keyof Omit<CookieConsent, "timestamp" | "version">) => boolean;
  revokeConsent: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
  isPreferencesOpen: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const CookieConsentProvider = ({ children }: { children: ReactNode }) => {
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const cookieConsent = useCookieConsent();

  const openPreferences = useCallback(() => setIsPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setIsPreferencesOpen(false), []);

  return (
    <CookieConsentContext.Provider
      value={{
        ...cookieConsent,
        openPreferences,
        closePreferences,
        isPreferencesOpen,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsentContext = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error("useCookieConsentContext must be used within CookieConsentProvider");
  }
  return context;
};
